/**
 * QoS 지표 지원 매트릭스 — 영상 소스 입력 유형별로 구조적으로 제공 불가한 EndpointStats 필드 정의.
 *
 * 상세: dev-docs/DES_CMN_채널상태정보_QoS수집및표현.md §4.2 "Caster 유형별 수집 가능 현황"
 *
 * "—" (해당 미디어 서버/Caster에서 제공 불가) 항목을 필드명 기준으로 매핑한다.
 * "지원되지 않음"과 "아직 수집되지 않음"을 구분하기 위한 UI 판정에 사용한다.
 */

import type { VideoSourceType } from './video-source.types'
import type { EndpointStats } from './endpoint-stats.types'

export type QosMetricKey = keyof EndpointStats

/**
 * 입력 유형별 구조적 미지원 지표 목록.
 *
 * - browser_camera / browser_screen : Browser caster — 전 지표 제공 가능
 * - opencv_device  / opencv_rtsp    : Python SDK — RTCP NACK/PLI 미측정
 * - gst_rtsp                        : C++ Headless Relay — Jitter/NACK/PLI 미측정
 * - onboard_rtsp                    : 온보드 C++ 캐스터 — 패스스루라 인코더 지표 없음
 */
export const UNSUPPORTED_QOS_METRICS: Record<VideoSourceType, readonly QosMetricKey[]> = {
  browser_camera: [],
  browser_screen: [],
  opencv_device:  ['nackCount', 'pliCount'],
  opencv_rtsp:    ['nackCount', 'pliCount'],
  gst_rtsp:       ['jitterMs', 'nackCount', 'pliCount'],
  // 패스스루 경로라 인코딩을 하지 않는다. PLI 를 받아도 키프레임을 만들 수 없으므로
  // pliCount 는 수신 여부만 의미가 있고 대응 결과를 반영하지 않는다(ADR-B32).
  onboard_rtsp:   ['jitterMs', 'nackCount', 'pliCount'],
}

/**
 * 해당 입력 유형에서 특정 QoS 지표가 지원되는지 여부.
 * sourceType이 미지정(undefined)이면 알 수 없으므로 true를 반환(기존 동작 유지).
 */
export function isQosMetricSupported(
  sourceType: VideoSourceType | undefined,
  metricKey: string,
): boolean {
  if (!sourceType) return true
  const unsupported = UNSUPPORTED_QOS_METRICS[sourceType]
  return !unsupported.includes(metricKey as QosMetricKey)
}
